import {BreadcrumbType} from 'platform/components';
import {match} from 'ts-pattern';

import {
  useCreateConfidentialOAuthClientMutation,
  useCreatePublicOAuthClientMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {generateOAuthClientSecret} from '../utils/generateOAuthClientSecret';
import {OAuthClientForm, OAuthClientFormValues} from './OAuthClientForm';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.oauthClient.labels.oauthClients'),
    href: settingsRoutes.oauthClientManagement,
  },
];

const defaultValues: Partial<OAuthClientFormValues> = {
  clientType: 'public',
};

export function AddOAuthClient() {
  const navigate = useNavigate();
  const [createPublicOAuthClient] = useCreatePublicOAuthClientMutation();
  const [createConfidentialOAuthClient] = useCreateConfidentialOAuthClientMutation();

  const handleSubmit = (values: OAuthClientFormValues) => {
    const createRequest = match(values.clientType)
      .with('public', () =>
        createPublicOAuthClient({
          createPublicOAuthClientRequestBody: {
            name: values.name,
            clientId: values.clientId,
            redirectUris: values.redirectUris,
          },
        }).unwrap()
      )
      .with('confidential', () =>
        createConfidentialOAuthClient({
          createConfidentialOAuthClientRequestBody: {
            name: values.name,
            clientId: values.clientId,
            clientSecret: generateOAuthClientSecret(),
            redirectUris: values.redirectUris,
          },
        }).unwrap()
      )
      .exhaustive();

    return createRequest
      .then((response) =>
        navigate(
          composePath(settingsRoutes.oauthClientManagementDetail, {
            params: {oauthClientId: response.id},
          })
        )
      )
      .catch(handleApiError);
  };

  return (
    <SettingsTemplate
      isCreating
      header={{title: i18n.t('entity.oauthClient.actions.newOAuthClient'), breadcrumbs}}
      data-testid={testIds.settings.oauthClientManagementCreate('page')}
    >
      <OAuthClientForm
        mode="create"
        defaultValues={defaultValues}
        submitButtonTitle={i18n.t('general.actions.create')}
        onSubmit={handleSubmit}
        onCancel={() => navigate(settingsRoutes.oauthClientManagement)}
        data-testid={testIds.settings.oauthClientManagementCreate('form')}
      />
    </SettingsTemplate>
  );
}
